import React from 'react';
import { withRouter } from 'react-router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useStore } from 'hooks';
import { VALIDATION, ROLE } from 'helpers/constants';
import { Button, ButtonClose } from 'components/ui/form';
import { Input, Select } from 'components/ui/formik';
import { UserService } from 'services';
import styles from './user.module.scss';

const fields = {
  role: ROLE.USER,
  inn: '',
  okpo: '',
  name: '',
  shortName: '',
  uAddress: '',
  pAddress: '',
  bankName: '',
  personalAccount: '',
  bankAddress: '',
  email: '',
  fio: '',
  phone: '',
};

const User = ({ onClose }) => {
  const { setUser, setLoading } = useStore();

  const initialValues = { ...fields };

  const onSubmit = async (values) => {
    const userService = new UserService({ setUser });

    if (Number(values.role) === ROLE.USER) {
      setLoading(true);
      const response = await userService.createUser({
        ...Object.keys(values).reduce((prev, next) => {
          const value = values[next];
          prev[next] = typeof value === 'string'
            ? value.trim()
            : value;
          return prev;
        }, {}),
      });
      setLoading(false);
      if (response && !response.toast) {
        onClose(true);
      }
    }

    if (Number(values.role) === ROLE.MANAGER) {
      setLoading(true);
      const response = await userService.createManager({
        ...Object.keys(values)
          .filter(key => ['inn', 'email', 'name'].includes(key))
          .reduce((prev, next) => {
            const value = values[next];
            prev[next] = typeof value === 'string'
              ? value.trim()
              : value;
            return prev;
          }, {}),
      });
      setLoading(false);
      if (response && !response.toast) {
        onClose(true);
      }
    }

    if (Number(values.role) === ROLE.ADMIN) {
      setLoading(true);
      const response = await userService.createAdmin({
        ...Object.keys(values)
          .filter(key => ['inn', 'email', 'name'].includes(key))
          .reduce((prev, next) => {
            const value = values[next];
            prev[next] = typeof value === 'string'
              ? value.trim()
              : value;
            return prev;
          }, {}),
      });
      setLoading(false);
      if (response && !response.toast) {
        onClose(true);
      }
    }
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={() => (
        Yup.lazy(({ role }) => {
          switch (Number(role)) {
            case ROLE.USER:
              return Yup.object().shape({
                ...Object.keys(fields).reduce((prev, next) => {
                  switch (next) {
                    case 'role':
                      prev[next] = Yup.mixed().required();
                      break;
                    case 'inn':
                      prev[next] = Yup.string()
                        .strict(true)
                        .trim(...VALIDATION.TRIM)
                        .test(...VALIDATION.SPACES_NOT_RESOLVED)
                        .required(...VALIDATION.REQUIRED);
                      break;
                    case 'phone':
                      prev[next] = Yup.string()
                        .test(...VALIDATION.PHONE)
                        .required(...VALIDATION.REQUIRED);
                      break;
                    case 'email':
                      prev[next] = Yup
                        .string()
                        .email(...VALIDATION.EMAIL)
                        .required(...VALIDATION.REQUIRED);
                      break;
                    default:
                      prev[next] = Yup
                        .string()
                        .strict(true)
                        .trim(...VALIDATION.TRIM)
                        .required(...VALIDATION.REQUIRED);
                  }
                  return prev;
                }, {}),
              });
            case ROLE.ADMIN:
            case ROLE.MANAGER:
              return Yup.object({
                inn: Yup.string()
                  .strict(true)
                  .trim(...VALIDATION.TRIM)
                  .test(...VALIDATION.SPACES_NOT_RESOLVED)
                  .required(...VALIDATION.REQUIRED),
                email: Yup
                  .string()
                  .email(...VALIDATION.EMAIL)
                  .required(...VALIDATION.REQUIRED),
                name: Yup
                  .string()
                  .strict(true)
                  .trim(...VALIDATION.TRIM)
                  .required(...VALIDATION.REQUIRED),
              });
            default:
              return null;
          }
        })
      )}
    >
      {({ handleSubmit, values }) => {
        const { role } = values;

        return (
          <form onSubmit={handleSubmit} className={styles.user} autoComplete="off">
            <div className={styles.main}>
              <div className={styles.title}>Добавление контрагента</div>
              <div className={styles.fields}>
                <div className={styles.field}>
                  <Select label="Роль" placeholder="Роль" name="role">
                    {ROLE.list().map(value => (
                      <option key={value} value={value}>{ROLE.toString(value)}</option>
                    ))}
                  </Select>
                </div>
                {Number(role) === ROLE.USER ? (
                  <>
                    <div className={styles.field}>
                      <Input label="УНП" placeholder="УНП" name="inn" />
                    </div>
                    <div className={styles.field}>
                      <Input label="ОКПО" placeholder="ОКПО" name="okpo" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Полное наименование" placeholder="Полное наименование" name="name" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Сокращенное наименование" placeholder="Сокращенное наименование" name="shortName" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Юридический адрес организации" placeholder="Юридический адрес организации" name="uAddress" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Почтовый адрес организации" placeholder="Почтовый адрес организации" name="pAddress" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Наименование Банка" placeholder="Наименование Банка" name="bankName" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Расчетный счет" placeholder="Расчетный счет" name="personalAccount" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Адрес банка" placeholder="Адрес банка" name="bankAddress" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Официальный E-mail организации" placeholder="Официальный E-mail организации" name="email" />
                    </div>
                    <div className={styles.field}>
                      <Input label="ФИО представителя организации" placeholder="ФИО представителя организации" name="fio" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Телефон представителя организации" placeholder="Телефон представителя организации" name="phone" />
                    </div>
                  </>
                ) : null}
                {Number(role) === ROLE.MANAGER || Number(role) === ROLE.ADMIN ? (
                  <>
                    <div className={styles.field}>
                      <Input label="Логин" placeholder="Логин" name="inn" />
                    </div>
                    <div className={styles.field}>
                      <Input label="Название" placeholder="Название" name="name" />
                    </div>
                    <div className={styles.field}>
                      <Input label="E-mail" placeholder="E-mail" name="email" />
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            <div className={styles.buttons}>
              <div>
                <Button type="submit">Добавить</Button>
              </div>
              <div>
                <ButtonClose onClick={() => onClose(false)}>Отменить</ButtonClose>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default withRouter(User);
